import axiosInstance, { clearCache } from '../utils/axiosConfig';
import {
  Product,
  ProductsResponse,
  Unit,
  UnitsResponse,
  ProductWarehouseInventoryResponse,
  Warehouse,
  WarehousesResponse,
  ProductAttribute,
  ProductVariantSearchResponse,
  BulkUpdateResponse,
  ProductChangeResponse
} from '../types/product';

// Warehouses API Endpoints
export const getWarehouses = async (
  page = 1,
  limit = 100,
  search?: string,
  context?: string
): Promise<WarehousesResponse> => {
  try {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    if (search) params.append('search', search);

    const headers: Record<string, string> = {};
    if (context) {
      headers['x-permission-context'] = context;
    }

    const response = await axiosInstance.get(`/warehouses?${params.toString()}`, { headers });
    return response.data;
  } catch (error) {
    console.error('Error fetching warehouses:', error);
    throw error;
  }
};

// Units API Endpoints
export const getUnits = async (
  page = 1,
  limit = 10,
  search?: string
): Promise<UnitsResponse> => {
  try {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    if (search) params.append('search', search);

    const response = await axiosInstance.get(`/units?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching units:', error);
    throw error;
  }
};

export const getUnitById = async (id: string): Promise<Unit> => {
  try {
    const response = await axiosInstance.get(`/units/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching unit ${id}:`, error);
    throw error;
  }
};

export const createUnit = async (unit: Omit<Unit, '_id'>): Promise<Unit> => {
  try {
    const response = await axiosInstance.post(`/units`, unit);
    return response.data;
  } catch (error) {
    console.error('Error creating unit:', error);
    throw error;
  }
};

export const updateUnit = async (id: string, unit: Partial<Unit>): Promise<Unit> => {
  try {
    const response = await axiosInstance.put(`/units/${id}`, unit);
    return response.data;
  } catch (error) {
    console.error(`Error updating unit ${id}:`, error);
    throw error;
  }
};

export const deleteUnit = async (id: string): Promise<{ message: string }> => {
  try {
    const response = await axiosInstance.delete(`/units/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting unit ${id}:`, error);
    throw error;
  }
};

export const getProducts = async (
  page = 1,
  limit = 10,
  search?: string,
  status?: string,
  forceRefresh = true
): Promise<ProductsResponse> => {
  try {
    // تنظيف تام للذاكرة المؤقتة لطلبات المنتجات
    if (forceRefresh) {
      clearCache(/products/);
    }
    
    const params = new URLSearchParams();
    params.append('page', page.toString());
    
    // تعديل: إذا كان الحد سالبًا، استخدم قيمة كبيرة (10000) للحصول على جميع المنتجات
    const finalLimit = limit < 0 ? 10000 : limit;
    params.append('limit', finalLimit.toString());
    
    if (search) params.append('search', search);
    if (status) params.append('status', status);
    
    // إضافة طابع زمني دائمًا لمنع التخزين المؤقت
    const timestamp = Date.now();
    params.append('_timestamp', timestamp.toString());

    console.log(`[${timestamp}] طلب المنتجات: صفحة ${page}, عدد ${limit}`);
    
    // طلب مباشر وجديد تمامًا في كل مرة
    const response = await axiosInstance.get(`/products`, {
      params,
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });
    
    console.log(`[${timestamp}] استجابة الخادم: ${response.status}`);
    console.log(`[${timestamp}] بيانات الصفحات: ${JSON.stringify(response.data?.pagination || {})}`);
    console.log(`[${timestamp}] عدد المنتجات المستلمة: ${response.data?.products?.length || 0}`);
    
    if (!response.data.products || !Array.isArray(response.data.products)) {
      console.warn(`[${timestamp}] هيكل بيانات المنتجات غير صالح:`, response.data);
      // إرجاع هيكل بيانات فارغ لمنع الأخطاء
      return {
        products: [],
        pagination: {
          total: 0,
          page: page,
          pages: 0
        }
      };
    }
    
    return response.data;
  } catch (error) {
    console.error('Error fetching products:', error);
    // مسح ذاكرة التخزين المؤقت في حالة الخطأ أيضًا
    clearCache(/products\?/);
    // Error handling is now centralized in axiosConfig
    throw error;
  }
};

// وظيفة البحث عن متغيرات المنتجات
export const searchProductVariants = async (
  searchTerm: string,
  limit = 20
): Promise<ProductVariantSearchResponse> => {
  try {
    const params = new URLSearchParams();
    params.append('q', searchTerm);
    params.append('limit', limit.toString());

    console.log('البحث عن متغيرات المنتجات بمصطلح:', searchTerm);
    
    const response = await axiosInstance.get(`/products/variants/search?${params.toString()}`);
    
    return response.data;
  } catch (error) {
    console.error('خطأ في البحث عن متغيرات المنتجات:', error);
    throw error;
  }
};

export const getProductById = async (id: string, context?: string): Promise<Product> => {
  try {
    // Timestamp is automatically added in axiosConfig for product endpoints
    const headers: Record<string, string> = {};
    if (context) {
      headers['x-permission-context'] = context;
      headers['X-Permission-Context'] = context;
      console.log(`[getProductById] Adding permission context: ${context}`);
    }
    
    const response = await axiosInstance.get(`/products/${id}`, { headers });
    return response.data;
  } catch (error) {
    console.error(`Error fetching product ${id}:`, error);
    throw error;
  }
};

export const createProduct = async (product: Omit<Product, '_id'>): Promise<Product> => {
  try {
    // Clear any cached product list data when creating a new product
    clearCache(/products\?/);
    const response = await axiosInstance.post(`/products`, product);
    return response.data;
  } catch (error: any) {
    console.error('Error creating product:', error);
    
    // معالجة أخطاء الباركود المكرر
    if (error.response?.status === 400) {
      if (error.response.data?.type === 'barcode_duplicate') {
        const customError = new Error(error.response.data.error || 'يوجد باركود مكرر');
        (customError as any).type = 'barcode_duplicate';
        (customError as any).details = error.response.data.details;
        throw customError;
      } else if (error.response.data?.details && Array.isArray(error.response.data.details)) {
        const customError = new Error('يوجد أخطاء في الباركود');
        (customError as any).type = 'barcode_validation';
        (customError as any).details = error.response.data.details;
        throw customError;
      }
    }
    
    throw error;
  }
};

export const updateProduct = async (id: string, product: Partial<Product>): Promise<Product> => {
  try {
    // Clear cached data for this specific product and the product list
    clearCache(new RegExp(`products/${id}`));
    clearCache(/products\?/);
    
    const response = await axiosInstance.put(`/products/${id}`, product);
    return response.data;
  } catch (error: any) {
    console.error(`Error updating product ${id}:`, error);
    
    // معالجة أخطاء الباركود المكرر
    if (error.response?.status === 400) {
      if (error.response.data?.type === 'barcode_duplicate') {
        const customError = new Error(error.response.data.error || 'يوجد باركود مكرر');
        (customError as any).type = 'barcode_duplicate';
        (customError as any).details = error.response.data.details;
        throw customError;
      } else if (error.response.data?.details && Array.isArray(error.response.data.details)) {
        const customError = new Error('يوجد أخطاء في الباركود');
        (customError as any).type = 'barcode_validation';
        (customError as any).details = error.response.data.details;
        throw customError;
      }
    }
    
    throw error;
  }
};

export const deleteProduct = async (id: string): Promise<{ message: string }> => {
  try {
    // Clear cached data for this specific product and the product list
    clearCache(new RegExp(`products/${id}`));
    clearCache(/products\?/);
    
    const response = await axiosInstance.delete(`/products/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting product ${id}:`, error);
    throw error;
  }
};

export const addVariant = async (
  productId: string,
  variant: any
): Promise<Product> => {
  try {
    // Clear cached data for this specific product
    clearCache(new RegExp(`products/${productId}`));
    
    const response = await axiosInstance.post(`/products/${productId}/variants`, variant);
    return response.data;
  } catch (error) {
    console.error(`Error adding variant to product ${productId}:`, error);
    throw error;
  }
};

export const updateVariant = async (
  productId: string,
  variantId: string,
  variant: any
): Promise<Product> => {
  try {
    // Clear cached data for this specific product
    clearCache(new RegExp(`products/${productId}`));
    
    const response = await axiosInstance.put(
      `/products/${productId}/variants/${variantId}`,
      variant
    );
    return response.data;
  } catch (error) {
    console.error(`Error updating variant ${variantId}:`, error);
    throw error;
  }
};

export const deleteVariant = async (
  productId: string,
  variantId: string
): Promise<Product> => {
  try {
    // Clear cached data for this specific product
    clearCache(new RegExp(`products/${productId}`));
    
    const response = await axiosInstance.delete(
      `/products/${productId}/variants/${variantId}`
    );
    return response.data;
  } catch (error) {
    console.error(`Error deleting variant ${variantId}:`, error);
    throw error;
  }
};

/**
 * Gets warehouse inventory information for a specific product
 * This fetches inventory data across all warehouses for the product
 */
export const getProductWarehouseInventory = async (
  productId: string,
  context?: string
): Promise<ProductWarehouseInventoryResponse> => {
  try {
    // Add timestamp parameter to prevent caching
    const timestamp = new Date().getTime();
    console.log(`Fetching warehouse inventory for product: ${productId} with context: ${context}`);
    
    const headers: Record<string, string> = {};
    if (context) {
      headers['x-permission-context'] = context;
      console.log(`Adding permission context header: ${context}`);
    }
    
    const response = await axiosInstance.get(`/inventory/product/${productId}`, { headers });
    console.log('Warehouse inventory response:', response.data);
    return response.data;
  } catch (error) {
    console.error(`Error fetching warehouse inventory for product ${productId}:`, error);
    throw error;
  }
};

/**
 * Helper function to safely map inventory data
 * This ensures consistent data structure regardless of API response format
 */
const mapInventoryData = (item: any, defaultUnit: string = 'قطعة') => {
  // Ensure all inventory values are properly typed
  const currentQty = typeof item.quantity === 'number' ? item.quantity : 0;
  const minQty = typeof item.minQuantity === 'number' ? item.minQuantity : 0;
  const reservedQty = typeof item.reservedQuantity === 'number' ? item.reservedQuantity : 0;
  
  return {
    warehouseId: item.warehouseId || (item.warehouse && item.warehouse._id) || '',
    warehouseName: item.warehouseName || (item.warehouse && item.warehouse.name) || 'غير معروف',
    warehouseLocation: item.warehouseLocation || (item.warehouse && item.warehouse.location) || '',
    availableQuantity: currentQty,
    quantity: currentQty,
    reservedQuantity: reservedQty,
    minimumQuantity: minQty,
    minQuantity: minQty,
    unit: item.unit || defaultUnit
  };
};

/**
 * Fetches all product attributes from the server
 */
export const getProductAttributes = async (): Promise<ProductAttribute[]> => {
  try {
    const response = await axiosInstance.get(`/product-attributes`);
    return response.data;
  } catch (error) {
    console.error('Error fetching product attributes:', error);
    throw error;
  }
};

export const getProductWithWarehouseInventory = async (productId: string, context?: string): Promise<Product> => {
  try {
    console.log(`Fetching product details for ID: ${productId} with context: ${context}`);
    
    // Get product details and warehouses in parallel
    const [product, warehousesResponse] = await Promise.all([
      getProductById(productId, context), // Pass context to getProductById
      getWarehouses(1, 100, undefined, context) // Get up to 100 warehouses with context
    ]);
    
    console.log('Product details retrieved successfully');
    console.log(`Found ${warehousesResponse.warehouses.length} warehouses`);
    
    // Create a map of all warehouses for easy access
    const allWarehouses = warehousesResponse.warehouses.filter(w => w.status === 'active');
    
    try {
      // Get warehouse inventory information with permission context
      console.log(`Fetching warehouse inventory for product: ${productId} with context: ${context}`);
      const inventoryResponse = await getProductWarehouseInventory(productId, context);
      console.log('Warehouse inventory retrieved successfully:', {
        inventoryCount: inventoryResponse.inventoryItems?.length || 0
      });
      
      // Map inventory data to product variants
      if (product.variants && product.variants.length > 0) {
        console.log(`Processing ${product.variants.length} variants`);
        
        // Process each variant to add warehouse inventory
        product.variants = product.variants.map(variant => {
          if (!variant._id) {
            console.warn('Variant missing _id, skipping inventory mapping');
            return variant;
          }
          
          // Find all inventory items for this variant
          const variantInventory = inventoryResponse.inventoryItems && inventoryResponse.inventoryItems.length > 0 ?
            inventoryResponse.inventoryItems.filter(item => item.variantId === variant._id) : [];
          
          console.log(`Found ${variantInventory.length} inventory items for variant ${variant._id}`);
          
          // Log inventory items for debugging
          console.log('Inventory items for variant', variant._id, ':', JSON.stringify(variantInventory, null, 2));
          
          // Create a map of warehouse inventory by warehouseId
          const warehouseInventoryMap = new Map();
          variantInventory.forEach(item => {
            // Ensure we have proper type conversions for all numeric values
            const currentQty = typeof item.quantity === 'number' ? item.quantity : 0;
            const minQty = typeof item.minQuantity === 'number' ? item.minQuantity : 0;
            const reservedQty = typeof item.reservedQuantity === 'number' ? item.reservedQuantity : 0;
            
            console.log(`Warehouse ${item.warehouseName} (${item.warehouseId}) quantity: ${currentQty}`);
            
            // Only add to map if we have a valid warehouseId
            if (item.warehouseId) {
              warehouseInventoryMap.set(item.warehouseId, {
                warehouseId: item.warehouseId,
                warehouseName: item.warehouseName || 'غير معروف',
                warehouseLocation: item.warehouseLocation || '',
                availableQuantity: currentQty,     // Set frontend "availableQuantity" to backend "quantity"
                quantity: currentQty,              // Also set quantity field for consistency
                reservedQuantity: reservedQty,
                minimumQuantity: minQty,          // Set frontend "minimumQuantity" to backend "minQuantity"
                minQuantity: minQty,              // Also set minQuantity field for consistency
                unit: item.unit || variant.unit || 'قطعة'
              });
            }
          });
          
          // Create warehouse inventory entries for ALL warehouses
          variant.warehouseInventory = allWarehouses.map(warehouse => {
            // If we have inventory data for this warehouse, use it
            if (warehouseInventoryMap.has(warehouse._id)) {
              return warehouseInventoryMap.get(warehouse._id);
            }
            
            // Otherwise create an entry with zero quantities
            return {
              warehouseId: warehouse._id || '',
              warehouseName: warehouse.name,
              warehouseLocation: warehouse.location || '',
              availableQuantity: 0, // Keep for frontend display
              quantity: 0,          // Add backend field
              reservedQuantity: 0,
              minimumQuantity: 0,   // Keep for frontend display
              minQuantity: 0,       // Add backend field
              unit: variant.unit || 'قطعة'
            };
          });
          
          return variant;
        });
      }
      
    } catch (inventoryError) {
      // Continue with just the product details if inventory fetch fails
      console.error(`Error fetching warehouse inventory: ${productId}`, inventoryError);
      console.log('Continuing with product details only (adding all warehouses with zero quantities)');
      
      if (product.variants && product.variants.length > 0) {
        // Add all warehouses with zero quantities
        product.variants = product.variants.map(variant => {
          // Only create warehouse inventory if it doesn't already exist
          if (!variant.warehouseInventory || variant.warehouseInventory.length === 0) {
            variant.warehouseInventory = allWarehouses.map(warehouse => {
              console.log(`Creating empty inventory for warehouse: ${warehouse.name} (${warehouse._id})`);
              return {
                warehouseId: warehouse._id || '',
                warehouseName: warehouse.name,
                warehouseLocation: warehouse.location || '',
                availableQuantity: 0, // Keep for frontend display
                quantity: 0,          // Add backend field
                reservedQuantity: 0,
                minimumQuantity: 0,   // Keep for frontend display
                minQuantity: 0,       // Add backend field
                unit: variant.unit || 'قطعة'
              };
            });
          } else {
            // Ensure all values are properly defined
            variant.warehouseInventory = variant.warehouseInventory.map(inventory => ({
              ...inventory,
              availableQuantity: typeof inventory.availableQuantity === 'number' ? inventory.availableQuantity : 0,
              quantity: typeof inventory.quantity === 'number' ? inventory.quantity : 0,
              reservedQuantity: typeof inventory.reservedQuantity === 'number' ? inventory.reservedQuantity : 0,
              minimumQuantity: typeof inventory.minimumQuantity === 'number' ? inventory.minimumQuantity : 0,
              minQuantity: typeof inventory.minQuantity === 'number' ? inventory.minQuantity : 0
            }));
          }
          return variant;
        });
      }
    }
    
    return product;
  } catch (error) {
    console.error(`Error fetching product with warehouse inventory: ${productId}`, error);
    
    // Error handling is now centralized in axiosConfig
    
    throw error;
  }
};

/**
 * الحصول على قالب تحديث المنتجات بالجملة
 * يحتوي على جميع المنتجات مع حقول الباركود والأسعار الحالية
 */
export const getBulkUpdateTemplate = async (): Promise<any> => {
  try {
    const response = await axiosInstance.post('/products/bulk-update/template');
    return response.data;
  } catch (error) {
    console.error('خطأ في الحصول على قالب تحديث المنتجات:', error);
    throw error;
  }
};

/**
 * تحميل قالب تحديث المنتجات بالجملة كملف إكسل
 * @param data البيانات التي سيتم تضمينها في ملف الإكسل
 */
export const downloadBulkUpdateExcelTemplate = (data: any[]): void => {
  try {
    // استخدام مكتبة xlsx أو أي مكتبة أخرى لتنزيل ملف Excel
    import('xlsx').then(XLSX => {
      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'تحديث المنتجات');
      
      // تعديل أعرض الأعمدة
      const columnWidths = [
        { wch: 30 }, // اسم المنتج
        { wch: 20 }, // الباركود
        { wch: 20 }, // الباركود الإضافي
        { wch: 15 }, // سعر التجزئة
        { wch: 15 }, // سعر الجملة
        { wch: 15 }, // سعر مخصص 1
        { wch: 15 }  // سعر مخصص 2
      ];
      worksheet['!cols'] = columnWidths;
      
      // تنزيل الملف
      XLSX.writeFile(workbook, 'تحديث-المنتجات.xlsx');
    });
  } catch (error) {
    console.error('خطأ في تنزيل قالب الإكسل:', error);
    throw error;
  }
};

/**
 * تحديث المنتجات بالجملة
 * @param products قائمة المنتجات المراد تحديثها
 */
export const bulkUpdateProducts = async (products: any[]): Promise<BulkUpdateResponse> => {
  try {
    // مسح ذاكرة التخزين المؤقت للمنتجات
    clearCache(/products\?/);
    
    const response = await axiosInstance.post('/products/bulk-update', {
      products
    });
    return response.data;
  } catch (error) {
    console.error('خطأ في تحديث المنتجات بالجملة:', error);
    throw error;
  }
};
